// src/utils/webrtcManager.js
class WebRTCManager {
  constructor(socket, userId, userName) {
    this.socket = socket;
    this.userId = userId;
    this.userName = userName;
    
    // Peer connections: Map(socketId -> RTCPeerConnection)
    this.peerConnections = new Map();
    
    // Local media stream
    this.localStream = null;
    
    // Audio context for processing
    this.audioContext = null;
    this.audioDestination = null;
    
    // Current channel
    this.currentChannelId = null;
    this.isSender = false;
    
    // Recording for saving voice messages
    this.mediaRecorder = null;
    this.recordedChunks = [];
    
    // Configuration
    this.config = {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
      ]
    };

    this.setupSocketListeners();
  }

  setupSocketListeners() {
    // Server tells SENDER to create offer for a specific peer
    this.socket.on("webrtc_create_offer_for_peer", async (data) => {
      console.log(`📝 Creating offer for peer ${data.peerName}`);
      
      try {
        const pc = await this.createPeerConnection(data.peerId, data.channelId, true);
        
        const offer = await pc.createOffer({
          offerToReceiveAudio: false,
          offerToReceiveVideo: false
        });
        
        await pc.setLocalDescription(offer);
        
        this.socket.emit("webrtc_offer", {
          channelId: data.channelId,
          offer: offer,
          targetSocketId: data.peerId
        });
        
        console.log(`✅ Sent offer to peer ${data.peerName}`);
      } catch (error) {
        console.error("Error creating offer:", error);
      }
    });

    this.socket.on("webrtc_incoming_call", async (data) => {
      console.log(`📞 Incoming WebRTC call from ${data.callerName}`);
      this.isSender = false;
      
      try {
        const pc = await this.createPeerConnection(data.callerId, data.channelId, false);
        console.log(`✅ Ready to receive audio from ${data.callerName}`);
      } catch (error) {
        console.error("Error handling incoming call:", error);
      }
    });

    this.socket.on("webrtc_offer", async (data) => {
      console.log(`📥 Received WebRTC offer from ${data.callerName}`);
      
      try {
        let pc = this.peerConnections.get(data.callerId);
        
        if (!pc) {
          pc = await this.createPeerConnection(data.callerId, data.channelId, false);
        }
        
        await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
        console.log("✅ Remote description set from offer");
        
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        
        this.socket.emit("webrtc_answer", {
          channelId: data.channelId,
          answer: answer,
          targetSocketId: data.callerId
        });
        
        console.log(`📤 Sent WebRTC answer to ${data.callerName}`);
      } catch (error) {
        console.error("Error handling offer:", error);
      }
    });

    this.socket.on("webrtc_answer", async (data) => {
      console.log(`📥 Received WebRTC answer from peer ${data.answererId}`);
      
      try {
        const pc = this.peerConnections.get(data.answererId);
        if (pc) {
          await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
          console.log("✅ Remote description set from answer");
        } else {
          console.warn("⚠️ No peer connection found for answerer");
        }
      } catch (error) {
        console.error("Error handling answer:", error);
      }
    });

    this.socket.on("webrtc_ice_candidate", async (data) => {
      try {
        const pc = this.peerConnections.get(data.senderId);
        if (pc && data.candidate) {
          await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
          console.log("✅ Added ICE candidate");
        }
      } catch (error) {
        console.error("Error adding ICE candidate:", error);
      }
    });

    this.socket.on("webrtc_call_ended", (data) => {
      console.log(`📴 Call ended by peer ${data.callerId}`);
      this.closePeerConnection(data.callerId);
    });
  }

  async createPeerConnection(peerId, channelId, isSender = false) {
    if (this.peerConnections.has(peerId)) {
      this.closePeerConnection(peerId);
    }

    const pc = new RTCPeerConnection(this.config);
    this.peerConnections.set(peerId, pc);

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        this.socket.emit("webrtc_ice_candidate", {
          channelId: channelId,
          candidate: event.candidate,
          targetSocketId: peerId
        });
        console.log("📤 Sent ICE candidate to peer");
      }
    };

    pc.ontrack = (event) => {
      console.log("🎵 Received remote audio track from peer", peerId);
      
      if (event.streams && event.streams[0]) {
        const remoteAudio = new Audio();
        remoteAudio.srcObject = event.streams[0];
        remoteAudio.autoplay = true;
        
        remoteAudio.play()
          .then(() => console.log("✅ Playing remote audio"))
          .catch(err => {
            console.error("❌ Audio play error:", err);
            document.addEventListener('click', () => {
              remoteAudio.play().catch(e => console.error("Retry play failed:", e));
            }, { once: true });
          });
      }
    };

    pc.onconnectionstatechange = () => {
      console.log(`WebRTC connection state: ${pc.connectionState}`);
      
      if (pc.connectionState === 'connected') {
        console.log("✅ WebRTC peer connection established");
      }
      
      if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        console.warn("⚠️ WebRTC connection failed");
        this.closePeerConnection(peerId);
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log(`ICE connection state: ${pc.iceConnectionState}`);
    };

    if (isSender && this.audioDestination) {
      this.audioDestination.stream.getAudioTracks().forEach(track => {
        pc.addTrack(track, this.audioDestination.stream);
        console.log(`📡 Added local audio track to peer ${peerId}`);
      });
    }

    return pc;
  }

  async startTransmission(channelId, volume = 1.0) {
    console.log(`🎙️ Starting WebRTC transmission on channel ${channelId}`);
    
    this.currentChannelId = channelId;
    this.isSender = true;
    this.recordedChunks = []; // Reset recorded chunks

    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
          channelCount: 1,
          latency: 0.01
        },
        video: false
      });

      console.log("✅ Microphone access granted");

      // Create audio context for processing
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
        latencyHint: 'interactive',
        sampleRate: 48000
      });

      const source = this.audioContext.createMediaStreamSource(this.localStream);
      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = volume;

      this.audioDestination = this.audioContext.createMediaStreamDestination();
      source.connect(gainNode);
      gainNode.connect(this.audioDestination);

      console.log("📡 Audio pipeline ready");

      // Start recording for saving voice message
      this.startRecording();

      // Notify server and wait for peer connections
      this.socket.emit("webrtc_start_call", { channelId });
      await new Promise(resolve => setTimeout(resolve, 500));

      console.log("✅ WebRTC call initiated, waiting for peers...");

      return true;
    } catch (error) {
      console.error("❌ Failed to start WebRTC transmission:", error);
      return false;
    }
  }

  startRecording() {
    try {
      // Record from the destination stream (after processing)
      this.mediaRecorder = new MediaRecorder(this.audioDestination.stream, {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000
      });

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };

      this.mediaRecorder.start(100); // Collect data every 100ms
      console.log("🔴 Started recording for voice message");
    } catch (error) {
      console.error("❌ Failed to start recording:", error);
    }
  }

  async stopTransmission(channelId) {
    console.log(`🔇 Stopping WebRTC transmission on channel ${channelId}`);

    // Stop recording and get the blob
    let audioBlob = null;
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      // Stop the recorder
      this.mediaRecorder.stop();
      
      // Wait for final data
      await new Promise(resolve => {
        this.mediaRecorder.onstop = () => {
          if (this.recordedChunks.length > 0) {
            audioBlob = new Blob(this.recordedChunks, { type: 'audio/webm' });
            console.log(`📦 Created audio blob: ${audioBlob.size} bytes`);
          }
          resolve();
        };
        
        // Timeout fallback
        setTimeout(resolve, 1000);
      });
    }

    // Notify server
    this.socket.emit("webrtc_end_call", { channelId });

    // Stop all tracks
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
    }

    // Close all peer connections
    for (const [peerId, pc] of this.peerConnections.entries()) {
      this.closePeerConnection(peerId);
    }

    // Close audio context
    if (this.audioContext) {
      await this.audioContext.close();
      this.audioContext = null;
    }

    this.currentChannelId = null;
    this.isSender = false;
    this.mediaRecorder = null;

    // Return the recorded audio blob
    return audioBlob ? { blob: audioBlob } : null;
  }

  closePeerConnection(peerId) {
    const pc = this.peerConnections.get(peerId);
    if (pc) {
      pc.close();
      this.peerConnections.delete(peerId);
      console.log(`🔌 Closed peer connection with ${peerId}`);
    }
  }

  async getStats(peerId) {
    const pc = this.peerConnections.get(peerId);
    if (pc) {
      const stats = await pc.getStats();
      const audioStats = [];
      
      stats.forEach(report => {
        if (report.type === 'inbound-rtp' || report.type === 'outbound-rtp') {
          audioStats.push({
            type: report.type,
            bytesReceived: report.bytesReceived,
            bytesSent: report.bytesSent,
            packetsLost: report.packetsLost,
            jitter: report.jitter,
            roundTripTime: report.roundTripTime
          });
        }
      });
      
      return audioStats;
    }
    return null;
  }

  cleanup() {
    console.log("🧹 Cleaning up WebRTC manager");
    
    if (this.currentChannelId) {
      this.stopTransmission(this.currentChannelId);
    }

    for (const [peerId, pc] of this.peerConnections.entries()) {
      this.closePeerConnection(peerId);
    }
  }
}

export default WebRTCManager;